// "How they match this role" on the company-facing candidate card. Derived
// from the role and the anonymized card profile only — the same facts the
// company can already see — so it never needs `score_breakdown` or any
// identity column. Mirrors the ranker's hard rules and salary rule
// (packages/db/src/ranking) in plain words; it does not score.
import { tzOffset } from '@date-fns/tz';

import type { CompanyCandidateCard } from '../projections.ts';
import { REGION_TAG_COUNTRIES, type RegionTag } from '../regions.ts';
import { regionLabel, regionShortLabel } from './format.ts';
import { EMPLOYMENT_LABELS } from './labels.ts';

type MatchCheckState = 'met' | 'unmet' | 'info';

export interface MatchCheck {
  state: MatchCheckState;
  title: string;
  detail: string;
}

export interface MatchCheckRole {
  skills: { name: string; required: boolean }[];
  employmentType: string | null;
  salaryMin: number | null;
  salaryMax: number | null;
  currency: string | null;
  regions: string[];
}

/** "40k", "1.2m" — compact amount for dense card copy. */
function compact(n: number): string {
  return new Intl.NumberFormat('en', { notation: 'compact', maximumFractionDigits: 1 })
    .format(n)
    .toLowerCase();
}

function money(n: number, currency: string | null): string {
  return currency ? `${currency} ${compact(n)}` : compact(n);
}

function roleRange(role: MatchCheckRole): string {
  const { salaryMin: lo, salaryMax: hi, currency } = role;
  const range =
    lo !== null && hi !== null ? `${compact(lo)}–${compact(hi)}` : `up to ${compact(hi!)}`;
  return currency ? `${currency} ${range}` : range;
}

/**
 * "UTC+8", "UTC+5:30", "UTC-2:30", "UTC" for an IANA zone at `at` (DST-aware);
 * the zone itself if unknown. Built from the offset in minutes, not ICU's
 * "shortOffset" text, which differs between ICU versions ("GMT" vs "GMT+0").
 */
export function utcOffsetLabel(timeZone: string, at = new Date()): string {
  const minutes = tzOffset(timeZone, at);
  if (Number.isNaN(minutes)) return timeZone;
  if (minutes === 0) return 'UTC';
  const abs = Math.abs(minutes);
  const mm = abs % 60;
  return `UTC${minutes < 0 ? '-' : '+'}${Math.floor(abs / 60)}${mm ? `:${String(mm).padStart(2, '0')}` : ''}`;
}

/** Which of the role's region codes lets this country in; null if none. */
export function matchingRegion(regions: string[], country: string): string | null {
  for (const code of regions) {
    if (code === country) return code;
    const members = REGION_TAG_COUNTRIES[code as Exclude<RegionTag, 'WORLDWIDE'>];
    if (members?.includes(country as never)) return code;
  }
  return null;
}

export function matchChecks(
  role: MatchCheckRole,
  profile: CompanyCandidateCard['profile'],
): MatchCheck[] {
  const checks: MatchCheck[] = [];
  const has = new Set(profile.skills.map((s) => s.name.toLowerCase()));
  const required = role.skills.filter((s) => s.required);
  const niceToHave = role.skills.filter((s) => !s.required);

  if (required.length) {
    const missing = required.filter((s) => !has.has(s.name.toLowerCase())).map((s) => s.name);
    checks.push({
      state: missing.length ? 'unmet' : 'met',
      title: `${required.length - missing.length} of ${required.length} required skills`,
      detail: missing.length ? `Missing ${missing.join(', ')}` : 'All required skills',
    });
  }

  for (const s of niceToHave) {
    const ok = has.has(s.name.toLowerCase());
    checks.push({
      state: ok ? 'met' : 'unmet',
      title: `${ok ? 'Has' : 'No'} ${s.name}`,
      detail: 'Nice to have',
    });
  }

  if (role.employmentType) {
    const name =
      (EMPLOYMENT_LABELS as Record<string, string>)[role.employmentType] ?? role.employmentType;
    const ok = profile.employmentTypes.includes(role.employmentType);
    checks.push({
      state: ok ? 'met' : 'unmet',
      title: ok ? `Open to ${name.toLowerCase()} work` : `Not open to ${name.toLowerCase()} work`,
      detail: ok ? `Matches ${name}` : `Role is ${name}`,
    });
  }

  if (profile.salaryMin !== null && role.salaryMax !== null) {
    const asks = `Asks from ${money(profile.salaryMin, profile.currency)} · your range ${roleRange(role)}`;
    if (profile.currency && role.currency && profile.currency !== role.currency) {
      // Different currencies are not compared (OPEN_QUESTIONS Q12).
      checks.push({ state: 'info', title: 'Salary in another currency', detail: asks });
    } else {
      const ok = profile.salaryMin <= role.salaryMax;
      checks.push({
        state: ok ? 'met' : 'unmet',
        title: ok ? 'Salary fits' : 'Asks above your range',
        detail: asks,
      });
    }
  }

  const offset = utcOffsetLabel(profile.timezone);
  const open = role.regions.length === 0 || role.regions.includes('WORLDWIDE');
  const region = open ? null : matchingRegion(role.regions, profile.country);
  checks.push(
    open
      ? {
          state: 'met',
          title: `Lives in ${regionLabel(profile.country)}`,
          detail: `${offset} · role open worldwide`,
        }
      : region
        ? { state: 'met', title: `Lives in ${regionShortLabel(region)}`, detail: offset }
        : {
            state: 'unmet',
            title: 'Outside your regions',
            detail: `${regionLabel(profile.country)} · ${offset}`,
          },
  );

  return checks;
}
